import styled from 'styled-components';
import { JRCCard } from '../JRCCard/JRCCard';
import React, { useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { Colors } from '../../styles/theme';

export interface JRCDropDownProps {
    trigger: React.ReactNode;
    dropdown: (collapseDropdown: () => void) => React.ReactNode;
    align?: 'left' | 'right';
    onToggle?: (isOpen: boolean) => void;
    withBorder?: boolean;
    borderColor?: Colors;
}

const Wrapper = styled.span`
    position: relative;
`;

const Trigger = styled.span`
    cursor: pointer;
`;

const DropdownWrapper = styled(JRCCard)<{ align?: 'left' | 'right'; withBorder?: boolean; borderColor?: Colors }>`
    position: absolute;
    margin: 8px 0 0 0;
    ${(props) => props.align === 'right' && 'right: 0'};
    width: max-content;
    border: ${({ withBorder, borderColor, theme }) =>
        withBorder ? '1px solid ' + (borderColor ? theme.color[borderColor] : theme.color.grey1) : 'none'};
    z-index: ${({ theme }) => theme.zIndex.dropdown};
`;

type MouseEvents = 'click' | 'mousedown' | 'mouseup';
type TouchEvents = 'touchstart' | 'touchend';

interface ClickAwayListenerProps extends React.HTMLAttributes<HTMLElement> {
    onClickAway: (event: MouseEvent | TouchEvent) => void;
    mouseEvent?: MouseEvents;
    touchEvent?: TouchEvents;
    as?: React.ElementType;
}

const ClickAwayListener = ({
    as = 'div',
    onClickAway,
    mouseEvent = 'click',
    touchEvent = 'touchend',
    ...props
}: ClickAwayListenerProps) => {
    const node = React.useRef<HTMLElement>(null);
    React.useEffect(() => {
        const handleEvents = (event: MouseEvent | TouchEvent): void => {
            if (node.current && node.current.contains(event.target as Node)) {
                return;
            }
            onClickAway(event);
        };
        document.addEventListener(mouseEvent, handleEvents);
        document.addEventListener(touchEvent, handleEvents);
        return () => {
            document.removeEventListener(mouseEvent, handleEvents);
            document.removeEventListener(touchEvent, handleEvents);
        };
    }, [mouseEvent, onClickAway, touchEvent]);

    return React.createElement(as, { ref: node, ...props });
};

export const JRCDropDown = ({ trigger, dropdown, align, onToggle, withBorder, borderColor }: JRCDropDownProps) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const handleMenuClick = () => {
        setIsOpen(!isOpen);
    };
    const handleClickAway = () => {
        setIsOpen(false);
    };

    useEffect(() => {
        if (onToggle) {
            onToggle(isOpen);
        }
    }, [isOpen, onToggle]);

    useEffect(() => {
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, []);

    const triggerId = useMemo(() => `trigger-${uuid()}`, []);
    const dropdownId = useMemo(() => `dropdown-${uuid()}`, []);

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Wrapper>
                <Trigger
                    role={'button'}
                    aria-expanded={isOpen}
                    aria-controls={dropdownId}
                    id={triggerId}
                    onClick={handleMenuClick}>
                    {trigger}
                </Trigger>
                {isOpen && (
                    <DropdownWrapper
                        borderColor={borderColor}
                        role={'region'}
                        id={dropdownId}
                        withBorder={withBorder}
                        align={align}
                        aria-labelledby={triggerId}>
                        {dropdown(() => setIsOpen(false))}
                    </DropdownWrapper>
                )}
            </Wrapper>
        </ClickAwayListener>
    );
};
